import {
  CreateSubmissionLinkMenuItem,
  DeleteNodeMenuItem,
  DownloadZipMenuItem,
  FavoriteNodeMenuItem,
  NewAlbumMenuItem,
  NewFolderMenuItem,
  RenameNodeMenuItem,
  ShareAlbumMenuItem,
  UploadToAlbumMenuItem,
  UploadToFolderMenuItem,
} from '@/components/menu-items/node-menu-items';
import { MenuProvider, MenuSeparator, contextPrimitives } from '@/components/menu-renderer';
import { type ChildTreeNode } from '@/lib/child-node';
import { DAMNodeType } from '@/types/tree';

/**
 * The full node context menu for a child rendered in the browser view's leading section —
 * the same item set the sidebar tree gives that node type (components/sidebar/tree/folder.tsx
 * and album.tsx), minus the two tree-only expand/collapse items which have no meaning here.
 *
 * Shared by the chip (tile view) and the list row so the two can never drift apart.
 *
 * Takes a `ChildTreeNode`, so the folder/album decision is the one already made by
 * `isChildTreeNode` upstream rather than a second narrowing of the same field. The switch is
 * exhaustive: adding a container kind to `ChildNodeType` fails the `never` assignment below
 * instead of silently falling through to the folder menu.
 */
export const ChildNodeMenuItems = ({
  node,
  parentPath,
}: {
  node: ChildTreeNode;
  /**
   * The currently browsed folder's own id and full name path — the parent of every node this
   * menu can be opened for. Passed straight to the upload items so they don't have to re-derive it
   * from the sidebar tree's node cache, which never holds a node shown only in this section.
   */
  parentPath?: { id: string; namePath: Array<string> };
}) => {
  const {
    node: { id: nodeId, name },
    type,
  } = node;

  switch (type) {
    case DAMNodeType.Album:
      return (
        <MenuProvider primitives={contextPrimitives}>
          <RenameNodeMenuItem node={node} />
          <DeleteNodeMenuItem node={node} danger />
          <MenuSeparator />
          <FavoriteNodeMenuItem node={node} />
          <MenuSeparator />
          <ShareAlbumMenuItem node={{ id: nodeId, name }} />
          <MenuSeparator />
          <UploadToAlbumMenuItem albumId={nodeId} albumName={name} parent={parentPath} />
          <DownloadZipMenuItem nodeIds={[nodeId]} name={name} sourceType={DAMNodeType.Album} />
        </MenuProvider>
      );
    case DAMNodeType.Folder:
      return (
        <MenuProvider primitives={contextPrimitives}>
          <NewFolderMenuItem node={node} parentId={nodeId} />
          <NewAlbumMenuItem node={node} parentId={nodeId} />
          <MenuSeparator />
          <RenameNodeMenuItem node={node} />
          <DeleteNodeMenuItem node={node} danger />
          <MenuSeparator />
          <FavoriteNodeMenuItem node={node} />
          <MenuSeparator />
          <UploadToFolderMenuItem folderId={nodeId} namePath={parentPath && [...parentPath.namePath, name]} />
          <CreateSubmissionLinkMenuItem node={{ id: nodeId, name }} />
          <DownloadZipMenuItem nodeIds={[nodeId]} name={name} sourceType={DAMNodeType.Folder} />
        </MenuProvider>
      );
    default: {
      const exhaustive: never = type;
      return exhaustive;
    }
  }
};
